import { Controller } from "stimulus";
import { Litepicker } from "litepicker";

const SEND_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z"/></svg>';
const CALENDAR_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 11h18"/></svg>';

interface MaybeLaterDetail {
  chainId: string;
  candidate: string;
  subject: string;
  reply: string;
  followUp?: {body: string; scheduledAt: string};
}

export default class extends Controller {
  static targets = [
    "composeStep", "successStep", "candidate", "reply", "toggle", "followUpFields",
    "followUpBody", "date", "timingNote", "submit", "submitLabel", "error", "recap",
    "successTitle", "successSub"
  ];

  declare readonly composeStepTarget: HTMLElement;
  declare readonly successStepTarget: HTMLElement;
  declare readonly candidateTarget: HTMLElement;
  declare readonly replyTarget: HTMLTextAreaElement;
  declare readonly toggleTarget: HTMLInputElement;
  declare readonly followUpFieldsTarget: HTMLElement;
  declare readonly followUpBodyTarget: HTMLTextAreaElement;
  declare readonly dateTarget: HTMLInputElement;
  declare readonly timingNoteTarget: HTMLElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly submitLabelTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly recapTarget: HTMLElement;
  declare readonly successTitleTarget: HTMLElement;
  declare readonly successSubTarget: HTMLElement;

  private chainId: string | null = null;
  private subject = "";
  private picker: Litepicker | null = null;
  private sending = false;

  connect(): void {
    const now = new Date();

    this.picker = new Litepicker({
      element: this.dateTarget,
      singleMode: true,
      // Tomorrow, not today: the server rejects anything not strictly in the future, so the picker
      // must not be able to produce a value it would refuse.
      minDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
      // 18 months, matching AiDraftRecommendation::MAX_FOLLOW_UP_MONTHS — one rule, not two.
      maxDate: new Date(now.getFullYear(), now.getMonth() + 18, now.getDate()),
      format: "YYYY-MM-DD",
      autoApply: true,
      dropdowns: {minYear: now.getFullYear(), maxYear: now.getFullYear() + 2, months: true, years: true}
    });
  }

  disconnect(): void {
    this.picker?.destroy();
    this.picker = null;
  }

  open(event: CustomEvent<MaybeLaterDetail>): void {
    const detail = event.detail;
    if (!detail) return;

    this.chainId = detail.chainId;
    this.subject = detail.subject || "";
    this.sending = false;

    this.candidateTarget.textContent = detail.candidate || "this candidate";
    this.replyTarget.value = detail.reply || "";

    const followUp = detail.followUp;
    this.toggleTarget.checked = !!followUp;
    this.toggleTarget.disabled = !followUp;
    this.followUpBodyTarget.value = followUp?.body || "";
    if (followUp?.scheduledAt) this.picker?.setDate(followUp.scheduledAt);
    this.timingNoteTarget.textContent = followUp
      ? "Timed to their reply. You approve it in your To-do on the day; nothing sends automatically."
      : "No follow-up was drafted for this reply.";

    this.clearError();
    this.toggleFollowUp();

    this.successStepTarget.hidden = true;
    this.composeStepTarget.hidden = false;
    this.element.classList.add("open");
    this.element.setAttribute("aria-hidden", "false");

    [this.replyTarget, this.followUpBodyTarget].forEach((el) => this.grow(el));

    this.replyTarget.focus();
  }

  close(): void {
    // Litepicker renders itself on <body>, outside this element, so hiding the modal does not hide
    // the calendar — it would be left floating over the page.
    this.picker?.hide();
    this.element.classList.remove("open");
    this.element.setAttribute("aria-hidden", "true");
  }

  closeOnBackdrop(event: Event): void {
    if (event.target === this.element) this.close();
  }

  closeOnEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape") return;
    // Never strand the user mid-send with a request already in flight.
    if (this.sending) return;

    this.close();
  }

  toggleFollowUp(): void {
    const on = this.toggleTarget.checked;

    this.followUpFieldsTarget.hidden = !on;
    this.submitLabelTarget.textContent = on ? "Send now & schedule" : "Send reply";
    if (on) this.grow(this.followUpBodyTarget);
  }

  autoGrow(event: Event): void {
    this.grow(event.target as HTMLTextAreaElement);
  }

  submit(): void {
    if (this.sending) return;

    const reply = this.replyTarget.value.trim();
    if (!reply) return this.showError("Write the reply that sends now.");

    const scheduling = this.toggleTarget.checked;
    const followUpBody = this.followUpBodyTarget.value.trim();
    const scheduledAt = this.dateTarget.value;

    if (scheduling && !followUpBody) return this.showError("Write the follow-up, or switch it off.");
    if (scheduling && !scheduledAt) return this.showError("Choose a date for the follow-up.");

    const payload: any = {reply: {subject: this.subject, body: this.htmlBody(reply)}};
    if (scheduling) {
      payload.follow_up = {subject: this.subject, body: this.htmlBody(followUpBody), scheduled_at: scheduledAt};
    }

    this.sending = true;
    this.submitTarget.disabled = true;
    this.clearError();

    this.post(`/email_chains/${this.chainId}/send_inbox_reply`, payload)
      .then((data) => {
        if (data.error) {
          this.sending = false;
          this.submitTarget.disabled = false;
          return this.showError(data.error);
        }

        this.showSuccess(data.follow_up?.scheduled_at);
      });
  }

  backToInbox(): void {
    window.location.reload();
  }

  // ---- internals ----

  private showSuccess(scheduledAt?: string): void {
    const name = this.candidateTarget.textContent || "the candidate";

    this.successTitleTarget.textContent = scheduledAt ? "Reply sent, follow-up scheduled" : "Reply sent";
    this.successSubTarget.textContent = scheduledAt
      ? "You'll get a To-do to approve the follow-up before it sends."
      : "One less thing in your inbox.";

    this.recapTarget.innerHTML = "";
    this.recapTarget.appendChild(this.recapRow(SEND_ICON, `Reply sent to ${name}`));
    if (scheduledAt) {
      this.recapTarget.appendChild(
        this.recapRow(CALENDAR_ICON, `Follow-up scheduled · ${this.humanDate(scheduledAt)}`)
      );
    }

    this.composeStepTarget.hidden = true;
    this.successStepTarget.hidden = false;
  }

  private recapRow(icon: string, text: string): HTMLElement {
    const row = document.createElement("div");
    row.className = "aidm-recap-row";

    const glyph = document.createElement("span");
    glyph.className = "aidm-recap-icon";
    glyph.setAttribute("aria-hidden", "true");
    glyph.innerHTML = icon;

    const label = document.createElement("span");
    label.textContent = text;

    row.append(glyph, label);
    return row;
  }

  private humanDate(iso: string): string {
    const date = new Date(`${iso}T00:00:00`);
    return isNaN(date.getTime())
      ? iso
      : date.toLocaleDateString(undefined, {month: "short", day: "numeric"});
  }

  private htmlBody(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML.replace(/\r\n|\r|\n/g, "<br>");
  }

  private grow(el: HTMLTextAreaElement): void {
    if (!el) return;

    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  private post(url: string, body: object): Promise<any> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken || "",
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    })
      .then((response) => response.json())
      .catch(() => ({error: "Something went wrong. Please try again."}));
  }

  private showError(message: string): void {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  private clearError(): void {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }
}
